<template>
  <div ref="selectContainer" :class="['cs__container', { 'active': isDropdownActive }, openDirection]">
    <button ref="selectInput" class="cs__select" @click="toggleDropdown" role="combobox" aria-controls="listbox"
      aria-haspopup="listbox" :aria-expanded="isDropdownActive.toString()">
      <div v-if="selectInput?.value " class="cs__select-selected-value">
        <div v-if="isColorOption" class="cs__option-color">
          <ImageField v-if="selectedColorOption.image" :key="selectedColorOption.image" :obj="{'url':selectedColorOption.image,'alt': selectedColorOption.name,'width':160,'height':160}"/>
          <span class="cs__option-name">{{selectInput.value}}</span>
        </div>
        <span v-else>{{ isSizeOption ? formatSizeOption(selectedOption) :  selectInput.value }}</span>
      </div>
      <span v-else class="cs__select-placeholder">{{ placeholder }}</span>
    </button>

    <BottomDrawer v-if="loadContentInBottomDrawer" :onCloseModal="toggleDropdown">
      <div class="cs__options" role="listbox" id="listbox">
        <div class="cs__option-placeholder">
          <span>Select Your Distance</span>
          <svg @click="toggleDropdown" class="cs__icon-close">
            <use xlink:href="#close"></use>
          </svg>
        </div>
        <template v-if="!isColorOption">
          <div v-for="option in options" :key="option" :class="['cs__option' , {'selected': selectedOption === option}]" @click="changeOption(option)" @keydown="handleKeydownOnOptions($event, option)" role="option" tabindex="0" :aria-selected="selectedOption === option ? true : false">
            {{ enablePrefixOnOption ? optionPrefix : null }} {{ isSizeOption ? formatSizeOption(option) :  option }} {{ enableSuffixOnOption ? optionSuffix : null }}
          </div>
        </template>
        <template v-else>
          <div v-for="(value,name,index) in options" :key="index" :class="['cs__option cs__option-color' , {'selected': selectedOption === name}]" @click="changeOption(name)" @keydown="handleKeydownOnOptions($event, name)" role="option" tabindex="0" :aria-selected="selectedOption === name ? true : false">
            <ImageField v-if="value.image" :key="value.image" :obj="{'url':value.image,'alt': name,'width':160,'height':160}"/>
            <span class="cs__option-name">{{name}}</span>
          </div>
        </template>
      </div>    
    </BottomDrawer>

    <div v-else-if="isDropdownActive" :class="['cs__options', openDirection]" role="listbox" id="listbox">
      <template v-if="!isColorOption">
        <div v-for="option in options" :key="option" :class="['cs__option' , {'selected': selectedOption === option}]" @click="changeOption(option)" @keydown="handleKeydownOnOptions($event, option)" role="option" tabindex="0" :aria-selected="selectedOption === option ? true : false">
          {{ enablePrefixOnOption ? optionPrefix : null }} {{ isSizeOption ? formatSizeOption(option) :  option }} {{ enableSuffixOnOption ? optionSuffix : null }}
        </div>
      </template>
      <template v-else>
        <div v-for="(value,name,index) in options" :key="index" :class="['cs__option cs__option-color' , {'selected': selectedOption === name}]" @click="changeOption(name)" @keydown="handleKeydownOnOptions($event, name)" role="option" tabindex="0" :aria-selected="selectedOption === name ? true : false">
          <ImageField v-if="value.image" :key="value.image" :obj="{'url':value.image,'alt': name,'width':160,'height':160}"/>
          <span class="cs__option-name">{{name}}</span>
        </div>
      </template>
    </div>

    <svg class="cs__icon">
      <use xlink:href="#icon-chevron-down"></use>
    </svg>
  </div>
</template>

<script setup>
  import { toRefs, ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
  import ImageField from "./image.vue";
  import BottomDrawer from '../global/bottom-drawer.vue';

  const props = defineProps({
    options: {
      type: Array,
      required: true,
    },

    optionPrefix: {
      type: String,
      default: ''
    },

    optionSuffix: {
      type: String,
      default: ''
    },

    placeholder: {
      type: String,
      default: 'Select a option'
    },

    callbackProps: {
      type: Object,
      default: {}
    },

    onDropdownSelect: {
      type: Function,
    },

    enableBodyOverflow: {
      type: Boolean,
      default: true
    },

    enablePrefixOnOption: {
      type: Boolean,
      default: true
    },

    enableSuffixOnOption: {
      type: Boolean,
      default: true
    },

    selectedValueProp: {
      type: String,
      default: ''
    },

    isColorOption: {
      type: Boolean,
      default: false
    },

    isSizeOption: {
      type: Boolean,
      default: false
    },

    dropdownOffsetTop: {
      type: Number,
      default: 0
    },
  })

  const { options, optionPrefix, optionSuffix, callbackProps, enableBodyOverflow, selectedValueProp, isColorOption, dropdownOffsetTop } = toRefs(props)
  const selectContainer = ref(null)
  const selectInput = ref(null)
  const selectedOption = ref(null)
  const isDropdownActive = ref(false)
  const openDirection = ref('down')
  var windowWidth = ref(window.innerWidth)

  onMounted(() => {
    window.addEventListener('resize', () => {
      windowWidth.value = window.innerWidth
    })

    document.addEventListener('click', handleClickOutside);

    if (selectedValueProp.value) {
      selectInput.value.value = optionPrefix.value + ' ' + selectedValueProp.value + ' ' + optionSuffix.value;
      selectedOption.value = selectedValueProp.value;
    }
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  defineExpose({ selectedOption })

  const selectedColorOption = computed(() => {
    if(!isColorOption.value) return

    return Object.values(options.value).find(option => option.name === selectedOption.value)
  })

  const loadContentInBottomDrawer = computed(() => {
    if (enableBodyOverflow.value && windowWidth.value < 768 && isDropdownActive.value) {
      return true;
    }

    return false;
  })

  const formatSizeOption = (size) => {
    return window.formattedSize(size)
  }

const toggleDropdown = () => {
  isDropdownActive.value = !isDropdownActive.value;

  if (isDropdownActive.value) {
    nextTick(() => {
      const selectRect = selectContainer.value.getBoundingClientRect();
      const dropdownHeight = 200; // Adjust if needed
      const spaceBelow = window.innerHeight - (selectRect.bottom + dropdownOffsetTop.value);
      const spaceAbove = selectRect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        openDirection.value = 'up';
      } else {
        openDirection.value = 'down';
      }
    });
  }
}

  const changeOption = (option) => {
    selectInput.value.value = optionPrefix.value + ' ' + option + ' ' + optionSuffix.value;
    selectedOption.value = option;

    if (props.onDropdownSelect) {
      props.onDropdownSelect(option, callbackProps.value);
    }

    toggleDropdown()
  }

  const handleClickOutside = (event) => {
    if (isDropdownActive.value && selectContainer.value && !selectContainer.value.contains(event.target)) {
      toggleDropdown();
    }
  };

  const handleKeydownOnOptions = (event, selOption) => {
    if (event.key === 'Escape') {
      toggleDropdown();
    } else if (event.key === 'Enter' || event.key === ' ') {
      changeOption(selOption);
      event.preventDefault(); // Prevent scrolling with Space key
    }
  };
</script>